import { Modal } from "@msys/ui";
import { Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Form, Formik } from "formik";
import { cloneDeep, uniqueId } from "lodash-es";
import React from "react";
import * as Yup from "yup";
import {
  AllowedValuesField,
  AllowedValuesFormValues,
  useAllowedValuesFieldValidationSchema,
} from "../form-fields/PropertyAllowedValuesField.js";
import {
  useValuesMediaBoolFieldValidationSchema,
  ValuesMediaBoolField,
  ValuesMediaBoolFormValues,
} from "../form-fields/PropertyValuesMediaBoolField.js";
import {
  NumberRangeSection,
  NumberRangeSectionFormValues,
  useNumberRangeValidationSchema,
} from "../NumberRangeSection.js";
import { PropValues, TypeFields } from "../properties.js";

type FormValues = TypeFields &
  PropValues &
  AllowedValuesFormValues &
  ValuesMediaBoolFormValues &
  NumberRangeSectionFormValues & { isComputed: boolean };

interface Props {
  values: FormValues;
  handleClose: () => void;
  handleComplete: (values: FormValues) => void | Promise<void>;
}

export const PropertyAllowedValuesModal = ({
  values,
  handleComplete,
  handleClose,
}: Props) => {
  const { t } = useTranslate(["Global", "QuoteItem"]);

  const initialValues = React.useMemo(() => cloneDeep(values), [values]);

  const allowedValuesSchema = useAllowedValuesFieldValidationSchema(values);
  const valuesMediaBoolSchema = useValuesMediaBoolFieldValidationSchema();
  const numberRangeSchema = useNumberRangeValidationSchema(values);

  const validationSchema = React.useMemo(
    () =>
      Yup.object().shape({
        ...allowedValuesSchema,
        ...valuesMediaBoolSchema,
        ...numberRangeSchema,
      }),
    [allowedValuesSchema, numberRangeSchema, valuesMediaBoolSchema]
  );

  const formId = React.useMemo(() => uniqueId(), []);

  const handleSubmit = async (values: FormValues) => {
    await handleComplete(values);
    handleClose();
  };

  return (
    <Formik<FormValues>
      validationSchema={validationSchema}
      validateOnMount
      onSubmit={handleSubmit}
      initialValues={initialValues}
    >
      {formikProps => (
        <Modal
          title={t("Allowed values", { ns: "QuoteItem" })}
          handleClose={handleClose}
          actionButtons={[
            {
              label: t("Cancel", { ns: "Global" }),
              handleClick: handleClose,
              buttonProps: {
                variant: "text",
                disabled: formikProps.isSubmitting,
              },
            },
            {
              label: t("Save", { ns: "Global" }),
              buttonProps: {
                form: formId,
                type: "submit",
                loading: formikProps.isSubmitting,
                disabled: !formikProps.isValid || !formikProps.dirty,
              },
            },
          ]}
        >
          <Form id={formId}>
            <Stack direction="column" spacing={1}>
              {formikProps.values.type !== "BOOLEAN" && !values.isComputed && (
                <AllowedValuesField
                  type={formikProps.values.type}
                  disabled={
                    (formikProps.values.type === "NUMBER" &&
                      (formikProps.values.setRange ||
                        formikProps.values.setStep)) ||
                    formikProps.isSubmitting
                  }
                />
              )}
              {formikProps.values.type === "BOOLEAN" && !values.isComputed && (
                <ValuesMediaBoolField disabled={formikProps.isSubmitting} />
              )}
              {formikProps.values.type === "NUMBER" && !values.isComputed && (
                <NumberRangeSection
                  disabled={formikProps.values.defineAllowedValues}
                />
              )}
            </Stack>
          </Form>
        </Modal>
      )}
    </Formik>
  );
};
